import React, { SyntheticEvent, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { Header } from '@/__core/layout';
import MainListModule from '@/__core/layout/module/MainListModule';
import { TabData } from '@/__core/types/TabData';
import { useTabStorage } from '@/modules/inventory/hooks/useTabStorage';
import { fetchCompanies } from '@/modules/organizations/redux/CompanyRedux';
import { getCompanyAutocompleteScopeFromStorage } from '@/utils/companyAutocompleteScope';
import AppListToolbarGlobal from '@/modules/finance/report_financial/components/FinancialReportToolbarGlobal';
import FinanceOverview from './FinancialOverview';
import FinancialOverviewTransactionsTab from './FinancialOverviewTransactionsTab';
import {
  readUiState,
  UI_STATE_KEYS,
  writeUiState,
} from '@/modules/commons/config/uiStateStorage';


const defaultTabs: TabData[] = [
  { id: 0, title: 'Báo cáo Thu Chi', type: 'list', closable: false, createdAt: Date.now() },
  { id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() },
];
const FINANCIAL_REPORT_PERIOD_PERSIST_CONFIG = {
  key: `${UI_STATE_KEYS.monthPeriodPicker}.financial-report.main`,
  namespace: 'xbox.ui.finance',
  version: 1,
  area: 'local' as const,
};

export default function FinancialReportModule() {
  const dispatch = useDispatch();
  const companies = useSelector((state: any) => state.company?.companies || []);

  const { tabs, activeTab, setActiveTab, addTab, closeTab } = useTabStorage({
    storageKey: 'inventory_stock_balance_tabs',
    defaultTabs,
    maxTabs: 12,
  });

  const activeTabData = tabs[activeTab];
  const displayTabs = useMemo(() => tabs.map((tab, index) => ({ ...tab, active: activeTab === index })), [tabs, activeTab]);

  const normalizedCompanyOptions = useMemo(
    () => (Array.isArray(companies) ? companies : [])
      .map((company: any) => ({
        company_id: company.company_id || company.id || '',
        company_name: company.company_name || company.name || '',
      }))
      .filter((company: { company_id: string }) => Boolean(company.company_id)),
    [companies]
  );

  const companyScope = useMemo(() => getCompanyAutocompleteScopeFromStorage({
    availableCompanies: normalizedCompanyOptions,
    includeSelectedCompanyFallback: true,
  }), [normalizedCompanyOptions]);

  const [filters, setFilters] = useState(() => {
    const restoredIso = readUiState<string | null>(FINANCIAL_REPORT_PERIOD_PERSIST_CONFIG, null);
    const restoredDate = restoredIso ? new Date(restoredIso) : null;

    return {
      selectedPeriod:
        restoredDate && !Number.isNaN(restoredDate.getTime())
          ? restoredDate
          : (new Date() as Date | null),
      companyId: '',
    };
  });

  const normalizeMonthParam = React.useCallback((value: string) => {
    const raw = String(value || '').trim();
    if (!raw) {
      return '';
    }

    const direct = raw.match(/^(\d{1,2})-(\d{4})$/);
    if (direct) {
      return `${Number(direct[1])}-${direct[2]}`;
    }

    const slash = raw.match(/^(\d{1,2})\/(\d{4})$/);
    if (slash) {
      return `${Number(slash[1])}-${slash[2]}`;
    }

    const any = raw.match(/(\d{1,2})[^\d]+(\d{4})/);
    if (any) {
      return `${Number(any[1])}-${any[2]}`;
    }

    return raw;
  }, []);

  const effectiveCompanyId = filters.companyId || companyScope.defaultCompanyId || '';

  const openNewStockBalanceTab = () => {
    addTab({
      id: Date.now(),
      title: `Báo cáo #${tabs.filter((tab) => tab.type === 'new-stock-balance').length + 1}`,
      type: 'new-stock-balance',
      closable: true,
      createdAt: Date.now(),
    });
  };

  const handleAddTab = () => openNewStockBalanceTab();

  const handleTabChange = (_event: SyntheticEvent, newIndex: number) => {
    if (tabs[newIndex]?.id === 10) return;
    setActiveTab(newIndex);
  };

  const handleCloseTab = (tabId: number) => {
    closeTab(tabId);
  };

  const handleSummaryCardClick = React.useCallback((payload: {
    key: string;
    title: string;
    type: string;
    month: string;
    companyId: string;
  }) => {
    const monthLabel = String(payload.month || '').trim();

    addTab({
      id: Date.now(),
      title: monthLabel ? `${payload.title} đến ${monthLabel}` : payload.title,
      type: 'new-financial-overview-transactions',
      closable: true,
      entityId: `${payload.key}-${payload.type}-${payload.month}-${payload.companyId}`,
      metadata: {
        queryType: payload.type,
        month: payload.month,
        companyId: payload.companyId,
      },
      createdAt: Date.now(),
    });
  }, [addTab]);

  const handleRow2CellClick = React.useCallback((payload: {
    month: string;
    type: 'receipt' | 'payment';
    companyId: string;
  }) => {
    const normalizedMonth = normalizeMonthParam(payload.month);
    const typeLabel = payload.type === 'receipt' ? 'Thu' : 'Chi';
    const monthLabel = String(normalizedMonth || payload.month || '').trim();
    addTab({
      id: Date.now(),
      title: monthLabel ? `${typeLabel} ${monthLabel}` : typeLabel,
      type: 'new-financial-overview-transactions',
      closable: true,
      entityId: `row2-${payload.type}-${normalizedMonth}-${payload.companyId}`,
      metadata: {
        queryType: payload.type,
        month: normalizedMonth,
        companyId: payload.companyId,
        monthParamKey: 'month',
      },
      createdAt: Date.now(),
    });
  }, [addTab, normalizeMonthParam]);

  const handleRow3CellClick = React.useCallback((payload: {
    month: string;
    transactionTypeId: string;
    transactionTypeName: string;
    companyId: string;
  }) => {
    const normalizedMonth = normalizeMonthParam(payload.month);
    const monthLabel = String(normalizedMonth || payload.month || '').trim();
    addTab({
      id: Date.now(),
      title: monthLabel ? `${payload.transactionTypeName} ${monthLabel}` : payload.transactionTypeName,
      type: 'new-financial-overview-transactions',
      closable: true,
      entityId: `row3-${payload.transactionTypeId}-${normalizedMonth}-${payload.companyId}`,
      metadata: {
        queryType: '',
        month: normalizedMonth,
        companyId: payload.companyId,
        monthParamKey: 'month',
        transactionTypeId: payload.transactionTypeId,
      },
      createdAt: Date.now(),
    });
  }, [addTab, normalizeMonthParam]);

  const handleToolbarFilterChange = (field: string, value: string) => {
    if (field !== 'company') {
      return;
    }
    setFilters((current) => ({
      ...current,
      companyId: value === 'all' || value === 'global' ? '' : value,
    }));
  };

  const toolbarCompanyFilter = filters.companyId || companyScope.defaultCompanyId || 'all';

  const handleSupplierDebtClick = React.useCallback((payload: {
    supplierId: string;
    supplierName: string;
    month: string;
    companyId: string;
  }) => {
    const normalizedMonth = normalizeMonthParam(payload.month);
    const monthLabel = String(normalizedMonth || payload.month || '').trim();
    addTab({
      id: Date.now(),
      title: monthLabel ? `${payload.supplierName} đến ${monthLabel}` : payload.supplierName,
      type: 'new-financial-overview-transactions',
      closable: true,
      entityId: `supplier-debt-${payload.supplierId}-${normalizedMonth}-${payload.companyId}`,
      metadata: {
        queryType: 'supplier_debt',
        month: normalizedMonth,
        companyId: payload.companyId,
        monthParamKey: 'to_month',
        supplierId: payload.supplierId,
      },
      createdAt: Date.now(),
    });
  }, [addTab, normalizeMonthParam]);

  React.useEffect(() => {
    dispatch(fetchCompanies() as any);
  }, [dispatch]);

  React.useEffect(() => {
    if (filters.companyId || !companyScope.defaultCompanyId) {
      return;
    }

    setFilters((current) => ({ ...current, companyId: companyScope.defaultCompanyId || '' }));
  }, [companyScope.defaultCompanyId, filters.companyId]);

  React.useEffect(() => {
    writeUiState<string | null>(
      FINANCIAL_REPORT_PERIOD_PERSIST_CONFIG,
      filters.selectedPeriod && !Number.isNaN(filters.selectedPeriod.getTime())
        ? filters.selectedPeriod.toISOString()
        : null
    );
  }, [filters.selectedPeriod]);

  return (
    <>
      <Header
        tabs={displayTabs}
        activeTab={activeTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='orders'
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {tabs.map((tab, index) => {
              const isActive = index === activeTab;
              return (
                <Box key={tab.id} sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}>
                  {tab.type === 'list' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            filters={{ company: toolbarCompanyFilter }}
                            onFilterChange={handleToolbarFilterChange}
                            selectedPeriod={filters.selectedPeriod}
                            onPeriodChange={(selectedPeriod) =>
                              setFilters((current) => ({ ...current, selectedPeriod }))
                            }
                            companies={
                              Array.isArray(companies)
                                ? companies.map((company: { id?: string; name?: string; company_id?: string; company_name?: string }) => ({
                                    id: String(company.id || company.company_id || ''),
                                    name: String(company.name || company.company_name || ''),
                                  }))
                                : []
                            }
                          />
                        }
                        dataTable={
                          <FinanceOverview
                            selectedPeriod={filters.selectedPeriod}
                            companyId={effectiveCompanyId}
                            onSummaryCardClick={handleSummaryCardClick}
                            onRow2CellClick={handleRow2CellClick}
                            onRow3CellClick={handleRow3CellClick}
                            onSupplierDebtClick={handleSupplierDebtClick}
                          />
                        }
                      />
                    </Box>
                  ) : (
                    <FinancialOverviewTransactionsTab
                      type={String((tab as any)?.metadata?.queryType || '')}
                      month={String((tab as any)?.metadata?.month || '')}
                      companyId={String((tab as any)?.metadata?.companyId || '')}
                      monthParamKey={(tab as any)?.metadata?.monthParamKey || 'to_month'}
                      transactionTypeId={String((tab as any)?.metadata?.transactionTypeId || '')}
                      supplierId={String((tab as any)?.metadata?.supplierId || '')}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </>
  );
}

